import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Col,
  DatePicker,
  InputNumber,
  Modal,
  notification,
  Row,
  Space,
  Switch,
  Typography,
} from 'antd';
import type { RangePickerProps } from 'antd/lib/date-picker';
import { fetchMissingEodPrices, selectors } from 'state';

type Props = {
  preset_name: string;
  preset_id: number;
  visible: boolean;
  hide: () => void;
};

const SeeMissingPricesModal: React.FC<Props> = ({
  preset_name,
  preset_id,
  visible,
  hide,
}) => {
  const dispatch = useDispatch();
  const waiting = useSelector(selectors.waiting);

  // date ranges
  const [bizRange, setBizRange] = useState<{
    moments: any[] | null;
    strings: [string, string] | null;
  }>({
    moments: null,
    strings: null,
  });
  const [sessRange, setSessRange] = useState<{
    moments: any[] | null;
    strings: [string, string] | null;
  }>({
    moments: null,
    strings: null,
  });

  // frequency
  const [frequency, setFrequency] = useState<number | null>(null);

  // NEW: toggles
  const [bizFetchEodOnly, setBizFetchEodOnly] = useState<boolean>(false);
  const [sessFetchEodOnly, setSessFetchEodOnly] = useState<boolean>(false);

  const onBizChange: RangePickerProps['onChange'] = (moments, dateStrings) =>
    setBizRange({
      moments: moments as any[] | null,
      strings: (dateStrings as [string, string]) ?? null,
    });

  const onSessChange: RangePickerProps['onChange'] = (moments, dateStrings) =>
    setSessRange({
      moments: moments as any[] | null,
      strings: (dateStrings as [string, string]) ?? null,
    });

  const onFreqChange = (value: number | undefined) => {
    if (value !== undefined) setFrequency(value);
  };

  const downloadBusinessCsv = () => {
    if (!bizRange.strings) return;
    dispatch(
      fetchMissingEodPrices({
        preset_id,
        start_date: bizRange.strings[0],
        end_date: bizRange.strings[1],
        preset_name,
        // if your API supports it, send the toggle flag:
        automatic_eod: bizFetchEodOnly,
        fetch_b_days: true,
        frequency: undefined,
      }) as any
    );
  };

  const downloadSessionCsv = () => {
    if (!sessRange.strings) {
      notification.warning({ message: 'Set date range' });
      return;
    }
    if (!frequency) {
      notification.warning({ message: 'Set frequency' });
      return;
    }
    dispatch(
      fetchMissingEodPrices({
        preset_id,
        start_date: sessRange.strings[0],
        end_date: sessRange.strings[1],
        preset_name,
        // if your API supports it, send the toggle flag:
        automatic_eod: sessFetchEodOnly,
        fetch_b_days: false,
        frequency: frequency,
      }) as any
    );
  };

  const onCancel = () => hide();

  return (
    <Modal
      title={`See missing prices for preset: ${preset_name}`}
      open={visible}
      onCancel={onCancel}
      footer={[]}
      width={800}
    >
      <Row gutter={30}>
        {/* Left column */}
        <Col
          span={12}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            paddingRight: 20,
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>See all business days</div>
            {/* NEW toggle */}
            <Space size="small">
              <Switch
                checked={bizFetchEodOnly}
                onChange={setBizFetchEodOnly}
                size="small"
              />
              <Typography.Text>
                View current automatic EoD selection instead
              </Typography.Text>
            </Space>

            <DatePicker.RangePicker
              format="YYYY-MM-DD"
              style={{ width: '100%', maxWidth: 250 }}
              value={bizRange.moments as any}
              onChange={onBizChange}
            />
            <Button
              loading={waiting}
              type="primary"
              onClick={downloadBusinessCsv}
              disabled={!bizRange.strings}
            >
              Download CSV
            </Button>
          </Space>
        </Col>

        {/* Right column */}
        <Col
          span={12}
          style={{
            borderLeft: '2px solid #eee',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            paddingRight: 20,
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>See Session specific days</div>
            {/* NEW toggle */}
            <Space size="small">
              <Switch
                checked={sessFetchEodOnly}
                onChange={setSessFetchEodOnly}
                size="small"
              />
              <Typography.Text>
                View current automatic EoD selection instead
              </Typography.Text>
            </Space>

            <DatePicker.RangePicker
              format="YYYY-MM-DD"
              style={{ width: '100%', maxWidth: 250 }}
              value={sessRange.moments as any}
              onChange={onSessChange}
            />

            <InputNumber
              style={{ width: 120 }}
              placeholder="Frequency"
              value={frequency ?? undefined}
              min={0}
              onChange={onFreqChange}
            />

            <Button
              loading={waiting}
              type="primary"
              onClick={downloadSessionCsv}
              disabled={!sessRange.strings || !frequency}
            >
              Download CSV
            </Button>
          </Space>
        </Col>
      </Row>

      <div style={{ marginTop: 25 }}>
        <Typography.Text type="secondary">
          (Until issue and maturity dates are implemented for all assets, these
          reports will not contain AFS/HTM assets)
        </Typography.Text>
      </div>
    </Modal>
  );
};

export default SeeMissingPricesModal;
